import { Autocomplete, Box, createFilterOptions, TextField, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';

import type { ClipMatchupTeam } from '../../services/toClipLibraryItems';
import { filterFieldSx } from './filterFieldSx';
import TeamCrest from './TeamCrest';

const ALL = 'all';

export type GameFilterOption = {
  value: string;
  /** "EDM vs FLA · 12/15/25" — what the input shows once one is picked. */
  label: string;
  gameDate: string;
  seasonLabel: string;
  matchup: { away: ClipMatchupTeam; home: ClipMatchupTeam };
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: GameFilterOption[];
};

// The label already carries both the abbreviations and the date, which is
// everything the row shows, so the default stringify is enough.
const filterGames = createFilterOptions<GameFilterOption>();

/**
 * Game picker. Each row is date, crest pair, fixture — the date leads because
 * the same two clubs meet several times a season and it is what separates them.
 *
 * Clearing the input is what selects "all games".
 */
const GameSelect = ({ value, onChange, options }: Props) => {
  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_event, next) => onChange(next?.value ?? ALL)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, other) => option.value === other.value}
      filterOptions={filterGames}
      groupBy={(option) => option.seasonLabel}
      selectOnFocus
      handleHomeEndKeys
      sx={{ width: 260 }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const { away, home } = option.matchup;

        return (
          <Box
            component="li"
            {...rest}
            key={key}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {/* Fixed width so the crests line up into a column across rows. */}
            <Typography sx={{ fontSize: 11, color: cx.text.tertiary, width: 52, flexShrink: 0 }}>
              {option.gameDate}
            </Typography>
            <TeamCrest abbreviation={away.abbreviation} logoUrl={away.logoUrl} size={20} />
            <TeamCrest abbreviation={home.abbreviation} logoUrl={home.logoUrl} size={20} />
            <Typography sx={{ fontSize: 12, color: cx.text.primary }}>
              {away.abbreviation} vs {home.abbreviation}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="All games"
          size="small"
          sx={filterFieldSx}
        />
      )}
    />
  );
};

export default GameSelect;
